import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2, Globe, Mic, MicOff, Phone, Paperclip, FileText, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { sendChatMessage } from '../../lib/api';
import { useLanguage } from '../../context/LanguageContext';

const WHATSAPP_NUMBER = '17864367307';
const WHATSAPP_MESSAGE = encodeURIComponent('¡Hola IQES! Me interesa obtener información sobre sus servicios de bajo voltaje.');
const PHONE_NUMBER = '17869813760';
const PHONE_DISPLAY = '786-981-3760';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ChatWidget() {
  const { language } = useLanguage();
  const [showContactModal, setShowContactModal] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [contactLang, setContactLang] = useState('en');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Check for speech recognition support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput(transcript);
        setIsListening(false);
      };
      
      recognitionRef.current.onerror = () => {
        setIsListening(false);
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    // Scroll to bottom when messages change
    if (scrollRef.current) {
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [messages, loading]);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      const errorMsg = selectedLang === 'es' 
        ? 'El archivo es muy grande. Máximo 10MB.'
        : 'File is too large. Maximum 10MB.';
      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
      return;
    }

    setUploadingFile(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_id', sessionId || 'temp-session');

      const response = await fetch(`${API_URL}/api/chat/upload`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setUploadedFiles(prev => [...prev, { name: file.name, id: data.file_id }]);
        
        // Add system message about uploaded file
        const uploadMsg = selectedLang === 'es'
          ? `📎 Archivo "${file.name}" subido correctamente. El especialista lo revisará.`
          : `📎 File "${file.name}" uploaded successfully. The specialist will review it.`;
        setMessages(prev => [...prev, { role: 'assistant', content: uploadMsg }]);
      } else {
        const errorMsg = selectedLang === 'es'
          ? 'Error al subir el archivo. Por favor intenta de nuevo.'
          : 'Error uploading file. Please try again.';
        setMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = selectedLang === 'es'
        ? 'Error al subir el archivo. Por favor intenta de nuevo.'
        : 'Error uploading file. Please try again.';
      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
    } finally {
      setUploadingFile(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const selectLanguage = (lang) => {
    setSelectedLang(lang);
    
    // Set recognition language
    if (recognitionRef.current) {
      recognitionRef.current.lang = lang === 'es' ? 'es-ES' : 'en-US';
    }
    
    // Mensaje de bienvenida instantáneo (no espera al servidor)
    const greeting = lang === 'es'
      ? '¡Hola! Soy Alex, asesor de IQES Low Voltage. ¿En qué puedo ayudarte hoy?'
      : "Hi! I'm Alex, advisor at IQES Low Voltage. How can I help you today?";
    setMessages([{ role: 'assistant', content: greeting }]);
  };

  const toggleListening = () => {
    if (!recognitionRef.current) return;
    
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (e) {
        console.error('Speech recognition error:', e);
      }
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await sendChatMessage({
        session_id: sessionId,
        message: userMessage,
        language: selectedLang,
      });

      setSessionId(response.data.session_id);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: response.data.response },
      ]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: selectedLang === 'es' 
            ? 'Lo siento, hubo un error. Por favor intente de nuevo.'
            : 'Sorry, there was an error. Please try again.',
        },
      ]);
    } finally {
      setLoading(false);
      // Mantener el focus en el input después de enviar
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  };

  const closeChat = () => {
    setIsOpen(false);
    setShowContactModal(false);
    setSelectedLang(null);
    setMessages([]);
    setSessionId(null);
    setUploadedFiles([]);
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }
    setIsListening(false);
  };

  return (
    <>
      {/* WhatsApp Button - Left side - Always visible */}
      <motion.a
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 left-4 sm:left-6 z-50 flex items-center gap-2 px-3 h-12 rounded-full bg-[#25D366] shadow-lg hover:scale-105 active:scale-95 transition-transform"
        data-testid="whatsapp-widget-btn"
      >
        <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="text-white text-xs font-medium">WhatsApp</span>
      </motion.a>

      {/* Chat/Contact Button - Right side */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        className="fixed bottom-6 right-4 sm:right-6 z-50 flex items-center gap-2 px-3 h-12 rounded-full shadow-lg hover:scale-105 active:scale-95 transition-transform"
        style={{ backgroundColor: '#003366' }}
        onClick={() => setShowContactModal(true)}
        data-testid="chat-widget-btn"
      >
        <MessageCircle className="w-6 h-6 text-white" />
        <span className="text-white text-xs font-medium">
          {language === 'es' ? 'Contáctenos' : 'Contact Us'}
        </span>
      </motion.button>

      {/* Contact Modal - Initial Options */}
      <AnimatePresence>
        {showContactModal && !isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-24 right-4 sm:right-6 z-50 w-[320px] max-w-[calc(100vw-32px)] bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden"
            data-testid="contact-modal"
          >
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-800">IQES</h3>
                <button 
                  onClick={() => setShowContactModal(false)}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <p className="text-gray-600 mt-1">
                {contactLang === 'es' ? '¿Cómo podemos ayudarte?' : 'How can we help you?'}
              </p>
            </div>

            {/* Language Selector */}
            <div className="px-4 pt-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                <Globe className="w-4 h-4" />
                <span>{contactLang === 'es' ? 'Seleccionar Idioma' : 'Select Language'}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setContactLang('en')}
                  className={`flex-1 py-2 px-4 rounded-lg border-2 font-medium transition-all flex items-center justify-center gap-2 ${
                    contactLang === 'en' 
                      ? 'border-[#003366] bg-[#003366] text-white' 
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span>🇺🇸</span> English
                </button>
                <button
                  onClick={() => setContactLang('es')}
                  className={`flex-1 py-2 px-4 rounded-lg border-2 font-medium transition-all flex items-center justify-center gap-2 ${
                    contactLang === 'es' 
                      ? 'border-[#003366] bg-[#003366] text-white' 
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span>🇪🇸</span> Spanish
                </button>
              </div>
            </div>

            {/* Legal Disclaimer */}
            <div className="px-4 pt-3">
              <p className="text-[10px] leading-tight text-gray-500">
                {contactLang === 'es' 
                  ? <>Al usar este chatbot, usted acepta: (1) los <a href="/terms" className="text-blue-600 underline" target="_blank">Términos</a> y <a href="/privacy" className="text-blue-600 underline" target="_blank">Política de Privacidad</a> de IQES; (2) que usamos proveedores de IA de terceros; (3) que podemos grabar sus interacciones; y (4) que acepta recibir SMS. Responda STOP para cancelar.</>
                  : <>By using this chatbot, you agree: (1) to IQES <a href="/terms" className="text-blue-600 underline" target="_blank">Terms</a> and <a href="/privacy" className="text-blue-600 underline" target="_blank">Privacy Policy</a>; (2) that we use third-party AI providers; (3) that we may record your interactions; and (4) that you consent to receive SMS. Reply STOP to opt-out.</>
                }
              </p>
            </div>

            {/* Action Buttons */}
            <div className="p-4 space-y-3">
              {/* Start Chat Button */}
              <button
                onClick={() => {
                  setShowContactModal(false);
                  setIsOpen(true);
                  if (contactLang) {
                    selectLanguage(contactLang);
                  }
                }}
                className="w-full py-3 px-4 rounded-lg font-semibold text-white flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#003366' }}
              >
                <MessageCircle className="w-5 h-5" />
                {contactLang === 'es' ? 'Iniciar Chat' : 'Start Chat'}
              </button>

              {/* Call Button */}
              <div className="text-center text-sm text-gray-500 my-2">
                {contactLang === 'es' ? 'O llámanos directamente:' : 'Or call us directly:'}
              </div>
              <a
                href={`tel:+${PHONE_NUMBER}`}
                className="w-full py-3 px-4 rounded-lg font-semibold text-white flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#f97316' }}
              >
                <Phone className="w-5 h-5" />
                {PHONE_DISPLAY}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-0 left-0 right-0 sm:bottom-6 sm:right-6 sm:left-auto z-50 w-full sm:w-[380px] sm:max-w-[calc(100vw-48px)] h-[85vh] sm:h-[520px] sm:max-h-[calc(100vh-120px)] bg-white sm:rounded-2xl rounded-t-2xl border border-gray-200 flex flex-col overflow-hidden shadow-2xl"
            data-testid="chat-window"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200" style={{ backgroundColor: '#003366' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white text-sm">IQES Assistant</h3>
                  <p className="text-xs text-white/70">Low Voltage Solutions</p>
                </div>
              </div>
              <button onClick={closeChat} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Language Selector */}
            {!selectedLang ? (
              <div className="flex-1 flex flex-col items-center justify-center p-6 gap-6 bg-gray-50">
                <Globe className="w-16 h-16 text-[#003366]" />
                <h2 className="text-xl font-semibold text-gray-800 text-center">
                  Select Language / Selecciona Idioma
                </h2>
                <div className="flex flex-col gap-3 w-full max-w-[280px]">
                  <Button
                    onClick={() => selectLanguage('en')}
                    className="w-full py-6 text-lg text-white rounded-xl"
                    style={{ backgroundColor: '#003366' }}
                    data-testid="chat-lang-en"
                  >
                    🇺🇸 English
                  </Button>
                  <Button
                    onClick={() => selectLanguage('es')}
                    className="w-full py-6 text-lg text-white rounded-xl"
                    style={{ backgroundColor: '#00A651' }}
                    data-testid="chat-lang-es"
                  >
                    🇪🇸 Español
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Messages */}
                <div 
                  ref={scrollRef}
                  className="flex-1 p-4 bg-gray-50 overflow-y-auto"
                  style={{ maxHeight: 'calc(100% - 140px)' }}
                >
                  <div className="space-y-4">
                    {messages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                            msg.role === 'user'
                              ? 'bg-[#003366] text-white rounded-br-md'
                              : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md shadow-sm'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        </div>
                      </div>
                    ))}
                    {loading && (
                      <div className="flex justify-start">
                        <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                          <div className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin text-[#003366]" />
                            <span className="text-sm text-gray-500">
                              {selectedLang === 'es' ? 'Escribiendo...' : 'Typing...'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Input */}
                <div className="p-4 pb-6 sm:pb-4 border-t border-gray-200 bg-white">
                  {/* Uploaded files indicator */}
                  {uploadedFiles.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {uploadedFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center gap-1 bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
                          <CheckCircle className="w-3 h-3" />
                          <span className="truncate max-w-[100px]">{file.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    {/* File Upload Button */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-shrink-0"
                      disabled={loading || uploadingFile}
                      title={selectedLang === 'es' ? 'Adjuntar archivo' : 'Attach file'}
                    >
                      {uploadingFile ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Paperclip className="w-5 h-5" />
                      )}
                    </Button>
                    
                    {/* Microphone Button */}
                    {speechSupported && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={toggleListening}
                        className={`flex-shrink-0 ${isListening ? 'bg-red-500/20 border-red-500 text-red-400' : ''}`}
                        disabled={loading}
                      >
                        {isListening ? (
                          <MicOff className="w-5 h-5" />
                        ) : (
                          <Mic className="w-5 h-5" />
                        )}
                      </Button>
                    )}
                    
                    <Input
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                      placeholder={selectedLang === 'es' ? 'Escribe tu mensaje...' : 'Type your message...'}
                      className="flex-1 bg-white border-2 border-gray-300 focus:border-[#003366] focus:ring-[#003366] text-gray-800 placeholder:text-gray-400"
                      disabled={loading}
                      style={{ backgroundColor: 'white' }}
                      autoFocus
                    />
                    <Button
                      onClick={handleSend}
                      disabled={!input.trim() || loading}
                      size="icon"
                      className="bg-[#003366] hover:bg-[#002244] flex-shrink-0"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  
                  {isListening && (
                    <p className="text-xs text-red-400 mt-2 text-center animate-pulse">
                      {selectedLang === 'es' ? '🎤 Escuchando...' : '🎤 Listening...'}
                    </p>
                  )}
                  
                  {/* File upload hint */}
                  <p className="text-xs text-gray-400 mt-2 text-center">
                    {selectedLang === 'es' 
                      ? '📎 Puedes adjuntar planos, fotos o documentos'
                      : '📎 You can attach plans, photos or documents'}
                  </p>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
